import React, { useEffect, useState } from 'react';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  Card,
  CardContent,
  Box,
  Button,
  Avatar,
  Typography,
  TableContainer,
  InputAdornment,
  TextField,
  CircularProgress,
  Chip,
  Stack,
  Badge,
  TablePagination,
  Alert,
} from '@mui/material';
import CarsIcon from '@mui/icons-material/DirectionsCar';
import SearchIcon from '@mui/icons-material/Search';
import { request } from '../../../../../utils/request';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDebounce } from '../../../../../utils/useDebounce';
import Brandcrumb from '../../../../../componentsUiBlock/Brandcrumb';

import DeleteConfirmDialog from '../../../../../componentsUiBlock/DeleteConfirmDialog';
import { useDeleteDialog } from '../../../../../utils/useDeleteDialog';
import { Car } from '../../../types';
import { baseUrlImage } from '../../../../../utils/apiClient';

interface HeadCell {
  id: keyof Car | 'action' | 'brand';
  label: string;
}

export const shadowImage = "https://pictures.dealer.com/m/mannchevroletbuickgmc/1459/a976281f56ff06d46dc390283aa14da6x.jpg?impolicy=downsize_bkpt&imdensity=1&w=520"

const headCells: HeadCell[] = [
  { id: 'brand', label: 'Brand' },
  { id: 'name', label: 'Car Name' },
  { id: 'variants', label: 'Variants' },
  { id: 'fuelTypes', label: 'Fuel Type' },
  { id: 'transmissions', label: 'Transmission' },
  { id: 'generations', label: 'Generations' },
  { id: 'action', label: 'Action' }
];

const CarsList: React.FC = () => {
  const [cars, setCars] = useState<Car[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<keyof Car | 'brand'>('name');
  const [search, setSearch] = useState('');

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const navigate = useNavigate();
  const debouncedSearch = useDebounce(search, 500);

  useEffect(() => {
    const getCarData = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams({
          page: (page + 1).toString(),
          limit: rowsPerPage.toString(),
        });

        if (debouncedSearch) {
          params.append('q', debouncedSearch);
        }

        const url = `compatibility/get-all-car-models?${params.toString()}`;

        const res = await request({
          url: url,
          method: 'get',
        });
        if (res.models) {
          setCars(res.models);
          setTotal(res.total)
        }
      } catch (error: any) {
        console.error(error?.message);
      } finally {
        setLoading(false);
      }
    };
    getCarData();
  }, [debouncedSearch, page, rowsPerPage]);

  const handleSort = (property: HeadCell['id']) => {
    if (property === 'action') return;

    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property as keyof Car | 'brand');
  };

  const sortedCars = [...cars].sort((a, b) => {
    let aValue: any;
    let bValue: any;

    if (orderBy === 'brand') {
      aValue = a.brand.name;
      bValue = b.brand.name;
    } else {
      aValue = a[orderBy as keyof Car];
      bValue = b[orderBy as keyof Car];
    }

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return order === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    return 0;
  });

  // Pagination handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const deleteCar = async (car: Car) => {
    try {
      const res = await request({
        url: "compatibility/delete-car-model",
        method: "delete",
        data: {
          id: car._id
        },
      });
      if (res?.status === "success") {
        toast.error(res?.message);
        // Remove the car from the list
        setCars(prev => prev.filter(c => c._id !== car._id));
        // If we deleted the last item on the page, go to previous page
        if (cars.length === 1 && page > 0) {
          setPage(page - 1);
        }
      } else {
        toast.error(res?.message);
      }
    } catch (error: any) {
      toast.error(error)
    }

  }

  const { open, loadingDelete, itemToDelete, openDialog, closeDialog, confirmDelete } = useDeleteDialog<Car>({
    onDelete: deleteCar,
  })

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
        <Brandcrumb />
      </Box>
      <Card elevation={4}>
        <CardContent sx={{ pb: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Button
              variant="contained"
              size='small'
              color="primary"
              onClick={() => navigate("/configuration/cars/addCar")}
              sx={{ flexShrink: 0 }}
              startIcon={<CarsIcon />}
            >
              Add New Car
            </Button>

            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={3}>

              <Badge badgeContent={total} showZero color="primary">
                <CarsIcon color="action" />
              </Badge>

              <TextField
                size="small"
                placeholder="Search by name or brand..."
                variant="outlined"
                sx={{ width: { xs: '100%', sm: 300 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                onChange={(e) => setSearch(e.target.value)}
              />

            </Box>


          </Box>
        </CardContent>

        <CardContent sx={{ pt: 1 }}>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {headCells.map((head) => (
                    <TableCell key={head.id} sx={{ fontWeight: 'bold' }}>
                      {head.id !== 'action' ? (
                        <TableSortLabel
                          active={orderBy === head.id}
                          direction={orderBy === head.id ? order : 'asc'}
                          onClick={() => handleSort(head.id)}
                        >
                          {head.label}
                        </TableSortLabel>
                      ) : (
                        head.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={24} />
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {debouncedSearch ? 'Searching...' : 'Loading car models...'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : sortedCars.length > 0 ? (
                  sortedCars.map((car) => (
                    <TableRow key={car._id} hover>
                      {/* Brand with logo */}
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar
                            alt={car.brand?.name}
                            sx={{
                              width: 32,
                              height: 32,
                              '& img': {
                                objectFit: 'contain',
                                padding: '3px',
                                borderRadius: '50%',
                              }
                            }}
                          >
                            <Box
                              component="img"
                              src={car.brand?.logo ? (baseUrlImage+car.brand?.logo) : 'https://cdn-icons-png.freepik.com/512/9942/9942310.png'  }
                              alt={`${car.brand?.name} Logo`}
                              sx={{ width: '100%', height: '100%', objectFit: 'contain', backgroundColor: "white" }}
                            />
                          </Avatar>
                          <Typography variant="body2">{car.brand?.name}</Typography>
                        </Box>
                      </TableCell>

                      {/* Car name with image */}
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar
                            variant="rounded"
                            alt={car.name}
                            sx={{
                              width: 50,
                              height: 32,
                              bgcolor: 'grey.100',
                              '& img': {
                                objectFit: 'contain',
                              }
                            }}
                          >
                            <Box
                              component="img"
                              src={car.baseImage ? baseUrlImage+car.baseImage : shadowImage}
                              alt={`${car.name} Image`}
                              sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            />
                          </Avatar>
                          <Typography variant="body2">{car.name}</Typography>
                        </Box>
                      </TableCell>

                      <TableCell>{car.variants.join(', ')}</TableCell>
                      <TableCell>{car.fuelTypes.join(', ')}</TableCell>
                      <TableCell>{car.transmissions.join(', ')}</TableCell>

                      {/* Generation Range as Chips */}
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {car.generations.map((g, i) => (
                            <Chip
                              key={i}
                              label={`${g.from} - ${g.to}`}
                              color="secondary"
                              variant="filled"
                              size="small"
                              sx={{
                                fontSize: '0.7rem',
                                height: 24,
                                mb: 0.5,
                                '& .MuiChip-label': {
                                  px: 1,
                                }
                              }}
                            />
                          ))}
                        </Stack>
                      </TableCell>

                      {/* Action Buttons */}
                      <TableCell>
                        <Box display="flex" flexDirection="column" gap={0.8}>
                          <Button
                            // size="large"
                            variant="text"
                            // startIcon={<PreviewIcon />}
                            onClick={() => navigate(`/configuration/cars/${car._id}`)}
                            sx={{
                              minWidth: 'auto',
                              fontSize: '0.60rem',
                              py: 0.1,
                              textWrap: 'nowrap',
                              px: 2,
                              borderRadius: 5,
                              border: '.8px solid #7f5ee666',
                              '&:hover': { backgroundColor: '#7f5ee666' }
                            }}
                          >
                            View / Edit
                          </Button>
                          <Button
                            size="small"
                            variant="text"
                            color="error"
                            onClick={() => openDialog(car)}
                            sx={{
                              minWidth: 'auto',
                              fontSize: '0.60rem',
                              py: 0.1,
                              px: 2,
                              borderRadius: 5,
                              border: '.8px solid #ff4d6e5e',
                              '&:hover': { backgroundColor: '#ff4d6e5e' }
                            }}
                          >
                            Delete
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        {search ? `No car models found for "${search}"` : 'No car models match your search criteria.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination Component - Added at the bottom */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          />
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}

      <DeleteConfirmDialog
        open={open}
        loading={loadingDelete}
        title="Delete Car"
        description={
          <>
            Are you sure you want to delete the car model <strong>"{itemToDelete?.name}"</strong> from <strong>{itemToDelete?.brand?.name}</strong>? <br /><br />
            <Alert severity="warning" sx={{ fontSize: "12px", py: "0px" }}>This action cannot be undone and all associated data will be permanently removed.</Alert>
          </>
        }
        onCancel={closeDialog}
        onConfirm={confirmDelete}
      />
    </>
  );
};

export default CarsList;